import { motion } from 'framer-motion'
import type { RestockSummary } from '../../lib/restockCalculator'
import type { ShopUrgency } from '../../lib/scheduleAnalyzer'

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  summary:        RestockSummary
  shopUrgency:    ShopUrgency
  onGenerateList: () => void
  onDismiss:      () => void
}

// ─── RestockBanner ────────────────────────────────────────────────────────────

export default function RestockBanner({ summary, shopUrgency, onGenerateList, onDismiss }: Props) {
  const days = shopUrgency.daysUntilShop ?? 0

  let title: string
  if (shopUrgency.urgency === 'upcoming') {
    title = `Coming up in ${days} day${days !== 1 ? 's' : ''}`
  } else if (days <= 0) {
    title = 'Today is your shop day'
  } else if (days === 1) {
    title = 'Tomorrow is your shop day'
  } else {
    title = `Shop in ${days} days`
  }

  // Items to preview — show urgent first, up to 3 across both lists
  const previewItems = [...summary.urgentItems, ...summary.lowItems].slice(0, 3)

  return (
    <motion.div
      className="mx-4 mb-3 rounded-2xl p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #6B9E78 0%, #5A8A67 100%)' }}
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {/* Dismiss */}
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center
                   rounded-full text-white/60 hover:text-white transition-colors text-sm"
        aria-label="Dismiss"
      >
        ✕
      </button>

      {/* Header */}
      <div className="flex items-center gap-2 mb-3 pr-8">
        <span className="text-xl">🛒</span>
        <p className="text-sm font-semibold text-white leading-tight">{title}</p>
      </div>

      {/* Stats pills */}
      <div className="flex flex-wrap gap-2 mb-3">
        <div
          className="px-3 py-1.5 rounded-full"
          style={{ background: 'rgba(255,255,255,0.20)' }}
        >
          <span className="text-xs font-semibold text-white">
            {summary.itemCount} item{summary.itemCount !== 1 ? 's' : ''} running low
          </span>
        </div>
        {summary.estimatedTotal > 0 && (
          <div
            className="px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.20)' }}
          >
            <span className="text-xs font-semibold text-white">
              Est. ${summary.estimatedTotal.toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {/* Item emoji preview with stagger */}
      <div className="flex items-center gap-1 mb-4">
        {previewItems.map((item, i) => (
          <motion.span
            key={item.id as string}
            className="text-xl leading-none"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.05, duration: 0.2, ease: 'easeOut' }}
          >
            {item.emoji as string}
          </motion.span>
        ))}
        {summary.itemCount > 3 && (
          <span className="text-xs text-white/70 ml-1">
            +{summary.itemCount - 3} more
          </span>
        )}
      </div>

      {/* Action button */}
      <button
        onClick={onGenerateList}
        className="w-full py-2.5 rounded-xl bg-white text-sm font-semibold
                   hover:bg-white/90 transition-colors"
        style={{ color: '#5A8A67' }}
      >
        View your list →
      </button>
    </motion.div>
  )
}
